import { AxiosResponse } from 'axios';

import { request } from '@cvt/clients/baseClient';
import { camelizeObject, snakizeObject } from '@cvt/helpers/object';

import {
  mapEducationProgress,
  mapGoalsCrudData,
  mapGradeResult,
  mapProfileData,
  mapSitePhoto,
  mapUtilityBill,
} from './hubClient.formatter';


const apiBaseUrl = import.meta.env.VITE__CVT_API_URL;

const getProfile = (): Promise<Hub.Profile> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/profile/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.ProfileApi>) => mapProfileData(res.data));
};

const updateGoals = (goals: Hub.Goals): Promise<Hub.Profile> => {
  return request({
    options: {
      url: `${apiBaseUrl}/hub/profile/`,
      method: 'PATCH',
      data: mapGoalsCrudData(goals),
    },
  }).then((res: AxiosResponse<Hub.ProfileApi>) => mapProfileData(res.data));
};

const getModules = (): Promise<Hub.Module[]> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/modules/`, method: 'GET' },
  }).then((res: AxiosResponse<Record<string, any>[]>) => res.data.map((m) => camelizeObject<Hub.Module>(m)));
};

const getEducationProgress = (): Promise<Hub.EducationProgress> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/modules/progress/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.EducationProgressApi>) => mapEducationProgress(res.data));
};

const markVideoWatched = (moduleId: string): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/modules/${moduleId}/video-watched/`, method: 'POST' },
  }).then(() => undefined);
};

const gradeQuiz = (moduleId: string, answers: string[]): Promise<Hub.GradeResult> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/quiz/${moduleId}/grade/`, method: 'POST', data: { answers } },
  }).then((res: AxiosResponse<Hub.GradeResultApi>) => mapGradeResult(res.data));
};

const getPhotos = (): Promise<Hub.SitePhoto[]> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/profile/photos/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.SitePhotoApi[]>) => res.data.map(mapSitePhoto));
};

const uploadPhoto = (category: string, file: File): Promise<Hub.SitePhoto> => {
  const data = new FormData();
  data.append('category', category);
  data.append('file', file);
  return request({
    options: {
      url: `${apiBaseUrl}/hub/profile/photos/`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data,
    },
  }).then((res: AxiosResponse<Hub.SitePhotoApi>) => mapSitePhoto(res.data));
};

const deletePhoto = (category: string): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/profile/photos/${category}/`, method: 'DELETE' },
  }).then(() => undefined);
};

const getUtilityBill = (): Promise<Hub.UtilityBill | null> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/profile/utility-bill/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.UtilityBillApi | null>) => (res.data ? mapUtilityBill(res.data) : null));
};

const uploadUtilityBill = (file: File): Promise<Hub.UtilityBill> => {
  const data = new FormData();
  data.append('file', file);
  return request({
    options: {
      url: `${apiBaseUrl}/hub/profile/utility-bill/`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data,
    },
  }).then((res: AxiosResponse<Hub.UtilityBillApi>) => mapUtilityBill(res.data));
};

const connectUtility = (utility?: string): Promise<Hub.UtilityConnectResult> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/utility/connect/`, method: 'POST', data: utility ? { utility } : {} },
  }).then((res: AxiosResponse<Record<string, any>>) => camelizeObject<Hub.UtilityConnectResult>(res.data));
};

const getUtilityStatus = (): Promise<Hub.UtilityConnection> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/utility/status/`, method: 'GET' },
  }).then((res: AxiosResponse<Record<string, any>>) => camelizeObject<Hub.UtilityConnection>(res.data));
};

const getUtilityBills = (): Promise<Hub.UtilityBillsResponse> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/utility/bills/`, method: 'GET' },
  }).then((res: AxiosResponse<Record<string, any>>) => camelizeObject<Hub.UtilityBillsResponse>(res.data));
};

const getReadiness = (): Promise<Hub.ReadinessResponse> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/readiness/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.ReadinessResponseApi>) => camelizeObject<Hub.ReadinessResponse>(res.data));
};

const getEnergyPrices = (state: string, period: string): Promise<Hub.EnergyPriceSeries> => {
  const query = `?state=${encodeURIComponent(state)}&period=${encodeURIComponent(period)}`;
  return request({
    options: { url: `${apiBaseUrl}/hub/energy-prices/${query}`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.EnergyPriceSeriesApi>) => camelizeObject<Hub.EnergyPriceSeries>(res.data));
};

const recordExploreActivity = (): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/explore-activity/`, method: 'POST' },
  }).then(() => undefined);
};

const getRecentActivity = (): Promise<Hub.ActivityItem[]> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/activity/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.ActivityItemApi[]>) => res.data.map((i) => camelizeObject<Hub.ActivityItem>(i)));
};

const getProjectBoard = (): Promise<Hub.ProjectItem[]> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/project-board/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.ProjectItemApi[]>) => res.data.map((i) => camelizeObject<Hub.ProjectItem>(i)));
};

const addProjectBoardItem = (item: { itemKey: string; itemType: string; label: string }): Promise<Hub.ProjectItem> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/project-board/`, method: 'POST', data: snakizeObject(item) },
  }).then((res: AxiosResponse<Hub.ProjectItemApi>) => camelizeObject<Hub.ProjectItem>(res.data));
};

const removeProjectBoardItem = (itemKey: string): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/project-board/${itemKey}/`, method: 'DELETE' },
  }).then(() => undefined);
};

const getConsultation = (): Promise<Hub.ConsultationStatus> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/consultation-booked/`, method: 'GET' },
  }).then((res: AxiosResponse<Hub.ConsultationStatusApi>) => camelizeObject<Hub.ConsultationStatus>(res.data));
};

const recordConsultation = (payload: { source: string; calendlyEventUri?: string }): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/consultation-booked/`, method: 'POST', data: snakizeObject(payload) },
  }).then(() => undefined);
};

const sendMessage = (payload: Hub.MessageInput): Promise<void> => {
  return request({
    options: { url: `${apiBaseUrl}/hub/messages/`, method: 'POST', data: snakizeObject(payload) },
  }).then(() => undefined);
};

export const hubClient = {
  getProfile,
  updateGoals,
  getModules,
  getEducationProgress,
  markVideoWatched,
  gradeQuiz,
  getPhotos,
  uploadPhoto,
  deletePhoto,
  getUtilityBill,
  uploadUtilityBill,
  connectUtility,
  getUtilityStatus,
  getUtilityBills,
  getReadiness,
  getEnergyPrices,
  recordExploreActivity,
  getRecentActivity,
  getProjectBoard,
  addProjectBoardItem,
  removeProjectBoardItem,
  getConsultation,
  recordConsultation,
  sendMessage,
};
